import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Container,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Language as LanguageIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { colors } from '../theme';
import { authAPI } from '../services/api';

const Header = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleLangMenuOpen = (event) => {
    setLangMenuAnchor(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangMenuAnchor(null);
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    handleLangMenuClose();
  };

  const handleLogout = () => {
    authAPI.logout();
  };

  const isAdmin = authAPI.isAdmin();

  const navItems = [
    { label: t('home'), path: '/', key: 'home' },
    { label: t('search'), path: '/search', key: 'search' },
    { label: t('aiChat'), path: '/ai', key: 'ai' },
    ...(isAdmin ? [
      { label: 'Upload', path: '/upload', key: 'upload' },
      { label: 'Tags', path: '/admin/tags', key: 'tags' },
      { label: 'Settings', path: '/admin/settings', key: 'settings' },
    ] : []),
  ];

  return (
    <AppBar position="sticky" sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Logo and Title */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: colors.royalBlue,
              flexGrow: isMobile ? 1 : 0,
              mr: 4,
            }}
          >
            <Box
              component="img"
              src="/logo.webp"
              alt="Lions Logo"
              sx={{ height: 40, mr: 2, display: { xs: 'none', sm: 'block' } }}
            />
            <Box>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                }}
              >
                {t('lionMagazine')}
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', gap: 2 }}>
              {navItems.map((item) => (
                <Button
                  key={item.key}
                  component={RouterLink}
                  to={item.path}
                  sx={{ color: colors.royalBlue, '&:hover': { backgroundColor: 'rgba(25,118,210,0.1)' } }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Language Selector */}
          <IconButton
            onClick={handleLangMenuOpen}
            sx={{ mr: 1, color: colors.royalBlue }}
          >
            <LanguageIcon />
          </IconButton>
          <Menu
            anchorEl={langMenuAnchor}
            open={Boolean(langMenuAnchor)}
            onClose={handleLangMenuClose}
          >
            <MenuItem
              onClick={() => handleLanguageChange('zh')}
              selected={i18n.language === 'zh'}
            >
              繁體中文
            </MenuItem>
            <MenuItem
              onClick={() => handleLanguageChange('en')}
              selected={i18n.language === 'en'}
            >
              English
            </MenuItem>
          </Menu>

          {/* Logout button (desktop) */}
          {!isMobile && (
            <IconButton
              onClick={handleLogout}
              sx={{ ml: 1, color: colors.royalBlue }}
              title="登出"
            >
              <LogoutIcon />
            </IconButton>
          )}

          {/* Mobile Menu */}
          {isMobile && (
            <>
              <IconButton
                onClick={handleMobileMenuOpen}
                sx={{ color: colors.royalBlue }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
              >
                {navItems.map((item) => (
                  <MenuItem
                    key={item.key}
                    component={RouterLink}
                    to={item.path}
                    onClick={handleMobileMenuClose}
                  >
                    {item.label}
                  </MenuItem>
                ))}
                <MenuItem onClick={() => { handleMobileMenuClose(); handleLogout(); }}>
                  登出
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
